import React, { useEffect, useMemo, useRef } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    makeStyles,
    TextField,
    Tooltip,
} from '@ellucian/react-design-system/core';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import LoadingButton from '../Common/LoadingButton';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';

const useStyles = makeStyles(() => ({
    DialogContentText: {
        whiteSpace: 'pre-wrap'
    },
    actionsContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: spacing40
    }
}));

const CallToActionDialog = (props) => {
    const { application, onClose, onAccept, onDecline, onConditionsMet, isOngoingApiCall, applicantComment, setApplicantComment } = props;
    const { formatMessage } = useIntl();
    const classes = useStyles();
    const primaryButtonRef = useRef(null);

    useEffect(() => {
        if (primaryButtonRef.current) {
            primaryButtonRef.current.focus();
        }
    }, []);

    const callToActionDialog = {
        [CONSTANTS.applicationDecisionStatus.admitted]: {
            title: 'applicantHomeAdmittedDialogTitle',
            description: 'applicantHomeAdmittedDialogDescription',
            primaryButton: { label: 'applicantHomeAdmittedDialogAcceptedButton', action: onAccept },
            secondaryButton: { label: 'applicantHomeAdmittedDialogDeclinedButton', action: onDecline }
        },
        [CONSTANTS.applicationDecisionStatus.conditionalAdmit]: {
            title: 'applicantHomeConditionalAdmitDialogTitle',
            description: 'applicantHomeConditionalAdmitDialogDescription',
            pathForDynamicValues: 'conditions',
            primaryButton: { label: 'applicantHomeConditionalAdmitDialogConditionsCompletedButton', action: onConditionsMet },
            showCommentInput: true,
            commentRequired: false,
            showCancelButton: true,
            commentPlaceholderDescription: 'applicantHomeConditionalAdmitConfirmationDialogCommentPlaceholder'
        },
        [CONSTANTS.applicationDecisionStatus.waitlist]: {
            title: 'applicantHomeWaitlistDialogTitle',
            description: 'applicantHomeWaitlistDialogDescription',
            primaryButton: { label: 'applicantHomeWaitlistDialogAcceptedButton', action: onAccept },
            secondaryButton: { label: 'applicantHomeWaitlistDialogDeclinedButton', action: onDecline }
        }
    };

    const dialogDescription = useMemo(() => {
        return callToActionDialog[application.decisionStatus]?.pathForDynamicValues
            ? formatMessage({ id: callToActionDialog[application.decisionStatus]?.description }, { conditions: application?.decisionReason })
            : formatMessage({ id: callToActionDialog[application.decisionStatus]?.description });
    }, [application.decisionReason]);

    return (
        <Dialog open={true}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
            aria-describedby="form-dialog-description"
            fullWidth
        >
            <DialogTitle 
                aria-label={formatMessage({ id: callToActionDialog[application.decisionStatus]?.title })} id="form-dialog-title"
            >
                {formatMessage({ id: callToActionDialog[application.decisionStatus]?.title })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="form-dialog-description"
                    className={classes.DialogContentText}
                    aria-label={dialogDescription}
                >
                    { dialogDescription }
                </DialogContentText>
                { application.offerStatus !== CONSTANTS.applicationOfferStatus.conditionsCompleted && callToActionDialog[application.decisionStatus]?.showCommentInput &&
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={10}
                        maxCharacters={200}
                        variant="outlined"
                        value={applicantComment}
                        placeholder={formatMessage({ id: callToActionDialog[application.decisionStatus]?.commentPlaceholderDescription })}
                        required={callToActionDialog[application.decisionStatus]?.commentRequired}
                        onChange={e => setApplicantComment(e.target.value)}
                        aria-label={formatMessage({ id: callToActionDialog[application.decisionStatus]?.commentPlaceholderDescription })}
                    />
                }
            </DialogContent>
            <DialogActions>
                <div className={classes.actionsContainer}>
                    { application.offerStatus !== CONSTANTS.applicationOfferStatus.conditionsCompleted &&
                        <Tooltip title={formatMessage({ id: callToActionDialog[application.decisionStatus]?.primaryButton?.label })}>
                            <LoadingButton
                                ref={primaryButtonRef}
                                onClick={callToActionDialog[application.decisionStatus]?.primaryButton?.action}
                                disabled={isOngoingApiCall}
                                aria-label={formatMessage({ id: callToActionDialog[application.decisionStatus]?.primaryButton?.label })}
                            >
                                {formatMessage({ id: callToActionDialog[application.decisionStatus]?.primaryButton?.label })}
                            </LoadingButton>
                        </Tooltip>
                    }
                    {callToActionDialog[application.decisionStatus].secondaryButton && (
                        <Tooltip title={formatMessage({ id: callToActionDialog[application.decisionStatus]?.secondaryButton?.label })}>
                            <LoadingButton
                                color="secondary"
                                onClick={callToActionDialog[application.decisionStatus]?.secondaryButton?.action}
                                isLoading={isOngoingApiCall}
                                aria-label={formatMessage({ id: callToActionDialog[application.decisionStatus]?.secondaryButton?.label })}
                            >
                                {formatMessage({ id: callToActionDialog[application.decisionStatus]?.secondaryButton?.label })}
                            </LoadingButton>
                        </Tooltip>
                    )}
                    {callToActionDialog[application.decisionStatus]?.showCancelButton && (
                        <Button
                            color="secondary"
                            onClick={onClose}
                            disabled={isOngoingApiCall}
                            aria-label={formatMessage({ id: 'applicantHomeCancelButton' })}
                        >
                            {formatMessage({ id: 'applicantHomeCancelButton' })}
                        </Button>
                    )}
                </div>
            </DialogActions>
        </Dialog>
    );
};

CallToActionDialog.propTypes = {
    application: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onAccept: PropTypes.func.isRequired,
    onDecline: PropTypes.func.isRequired,
    onConditionsMet: PropTypes.func.isRequired,
    isOngoingApiCall: PropTypes.bool.isRequired,
    applicantComment: PropTypes.string.isRequired,
    setApplicantComment: PropTypes.func.isRequired
};

export default CallToActionDialog;
